import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sun } from "lucide-react";

export const AddToMyDayDialog = ({ project, onClose }) => {
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [checklistId, setChecklistId] = useState("");
  const [newBoard, setNewBoard] = useState("");
  const [newChecklist, setNewChecklist] = useState("");
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/todo").then((r) => { setBoards(r.data.boards); const b = r.data.boards[0]; if (b) { setBoardId(b.board_id); setChecklistId(b.checklists[0]?.checklist_id || ""); } else setBoardId("__new"); }); }, []);
  const board = boards.find((b) => b.board_id === boardId);
  const needNewChecklist = checklistId === "__new" || (board && board.checklists.length === 0);
  const valid = title.trim() && (boardId === "__new" ? newBoard.trim() : boardId) && (boardId === "__new" || needNewChecklist ? newChecklist.trim() : checklistId);

  const save = async () => {
    setBusy(true);
    try {
      let bid = boardId, cid = checklistId;
      if (bid === "__new") { bid = (await api.post("/todo/boards", { title: newBoard.trim() })).data.board_id; cid = "__new"; }
      if (cid === "__new" || !cid) cid = (await api.post("/todo/checklists", { board_id: bid, title: newChecklist.trim() })).data.checklist_id;
      await api.post("/todo/items", { checklist_id: cid, title: title.trim(), note: note.trim() || null, due_date: due || null, project_id: project.project_id });
      toast.success("Added to My Day — private to-do, no impact on the project workflow");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-to-my-day-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sun className="h-5 w-5 text-amber-500" />Add to My Day</DialogTitle>
          <DialogDescription>Creates a private personal to-do linked to <span className="font-mono">{project.project_number}</span>. It does not create a project action or task and has no effect on phases, ERP readiness or reports.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Board</Label>
              <Select value={boardId} onValueChange={(v) => { setBoardId(v); const b = boards.find((x) => x.board_id === v); setChecklistId(b?.checklists[0]?.checklist_id || "__new"); }}>
                <SelectTrigger data-testid="my-day-board-select"><SelectValue placeholder="Choose board" /></SelectTrigger>
                <SelectContent>{boards.map((b) => <SelectItem key={b.board_id} value={b.board_id}>{b.title}</SelectItem>)}<SelectItem value="__new">+ New board…</SelectItem></SelectContent>
              </Select>
              {boardId === "__new" && <Input className="mt-2" placeholder="Board title" value={newBoard} onChange={(e) => setNewBoard(e.target.value)} data-testid="my-day-new-board-input" />}
            </div>
            <div><Label className="text-xs">Checklist</Label>
              <Select value={boardId === "__new" ? "__new" : checklistId} onValueChange={setChecklistId} disabled={boardId === "__new"}>
                <SelectTrigger data-testid="my-day-checklist-select"><SelectValue placeholder="Choose checklist" /></SelectTrigger>
                <SelectContent>{(board?.checklists || []).map((c) => <SelectItem key={c.checklist_id} value={c.checklist_id}>{c.title}</SelectItem>)}<SelectItem value="__new">+ New checklist…</SelectItem></SelectContent>
              </Select>
              {(boardId === "__new" || needNewChecklist) && <Input className="mt-2" placeholder="Checklist title" value={newChecklist} onChange={(e) => setNewChecklist(e.target.value)} data-testid="my-day-new-checklist-input" />}
            </div>
          </div>
          <div><Label className="text-xs">Title *</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`e.g. Call ${project.customer_name} about drawings`} data-testid="my-day-title-input" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Due date (optional)</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="my-day-due-input" /></div>
            <div><Label className="text-xs">Linked project</Label><div className="h-9 flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-mono" data-testid="my-day-linked-project">{project.project_number}</div></div>
          </div>
          <div><Label className="text-xs">Note (optional)</Label><Textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="my-day-note-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!valid || busy} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="my-day-save-btn">Add to My Day</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
